import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { LinkButton } from 'ui/Buttons';
import { ColumnDefinition, RowData } from '@scope/scope-one-ui/core/DataTable/DataTable.d';
import { useToasts } from '@scope/scope-one-ui/core/Messages/context';
import {
  ActionsMenu,
  Box,
  Card,
  DataTable,
  IconButton,
  IconTextButton,
  StatusBadge,
  Typography,
} from '@scope/scope-one-ui/core';
import LockIcon from '@scope/scope-one-ui/assets/icons/lock.svg';
import ClockIcon from '@scope/scope-one-ui/assets/icons/clock.svg';
import { useDebouncedCallback } from 'use-debounce/esm';
import { formatDate } from 'shared/functions';
import AddUserIcon from '../../../assets/icons/user-plus.svg';
import ArrowLeft from '../../../assets/icons/arrow-left.svg';
import UnlockIcon from '../../../assets/icons/unlock.svg';
import {
  activateRatingUsers,
  deactivateRatingUsers,
  getRating,
  getRatingUsers,
} from './services/rsrService';
import {
  Rating,
  RatingStatus,
  RatingStatuses,
  RatingUser,
  SubscriberConfirmation,
  SubscriberConfirmations,
  SubscriptionRatingType,
  UserStatus,
  UserStatuses,
} from './types';
import { UserRatingStatusLog } from './Modals/UserRatingStatusLog';
import UserStatusIndicator from './components/UserStatusIndicator';
import SubscriberConfirmationIndicator from './components/SubscriberConfirmationIndicator';
import RatingStatusIndicator from './components/RatingStatusIndicator';
import { SetSubscriberConfirmationModal } from './Modals/SetSubscriberConfirmationModal';
import { AssignUser } from './Modals/AssignUser';
import { useRSR } from '../../../shared/hooks';
import RefreshIcon from '../../../assets/icons/refresh-cw.svg';
import { ConfirmModal } from './Modals/ConfirmModal';

type RatingUsersListParams = {
  ratingType: SubscriptionRatingType;
  ratingId: string;
};

export const RatingUsersList: FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { error, success } = useToasts();
  const { ratingType = 'rsr', ratingId = '' } = useParams<RatingUsersListParams>();
  const [isAddModalOpen, setAddModalOpen] = useState(false);
  const [isLogModalOpen, setLogModalOpen] = useState(false);
  const [rating, setRating] = useState<Rating>();
  const [isSubscriberConfirmationModalOpen, setSubscriberConfirmationModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<RatingUser>();
  const [confirmType, setConfirmType] = useState<'APPROVE' | 'REVOKE'>('APPROVE');

  const fetchUsers = async () => {
    getRatingUsers(
      ratingType,
      ratingId,
      pagination.page || 1,
      pagination.limit,
      {
        sort: sort.field,
        dir: sort.order,
      },
      searchedValue,
    )
      .then((response) => {
        setRows(buildRows(response.data as RatingUser[]));
        setTotal(response.total);
      })
      .catch((er) => {
        error({ title: '', content: 'Error loading users information' });
      })
      .finally(() => setLoading(false));
  };

  const fetchRating = async () => {
    getRating(ratingType, ratingId)
      .then((resp) => {
        setRating(resp);
      })
      .catch((er) => {
        error({ title: '', content: 'Error loading rating information' });
      });
  };

  const activateUsers = async (userIds: string[]) => {
    if (rating) {
      setLoading(true);
      try {
        const res = await activateRatingUsers(ratingType, ratingId, rating.entityType, userIds);
        if (res) {
          success({ title: '', content: t('ADMIN.SNACKS.RATING_USERS_APPROVED') });
          fetchUsers();
        }
      } catch (e) {
        error({ title: '', content: t('ADMIN.SNACKS.RATING_USERS_APPROVE_FAILED') });
      } finally {
        setLoading(false);
      }
    }
  };

  const deactivateUsers = async (userIds: string[]) => {
    if (rating) {
      setLoading(true);
      try {
        const res = await deactivateRatingUsers(ratingType, ratingId, rating.entityType, userIds);
        if (res) {
          success({ title: '', content: t('ADMIN.SNACKS.RATING_USERS_REVOKED') });
          fetchUsers();
        }
      } catch (e) {
        error({ title: '', content: t('ADMIN.SNACKS.RATING_USERS_REVOKE_FAILED') });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleAddUser = (added: boolean) => {
    setAddModalOpen(false);
    if (added) {
      fetchUsers();
    }
  };

  const showuserStatusLog = (user: RatingUser) => {
    setSelectedUser(user);
    setLogModalOpen(true);
  };

  const showSubscriberConfirmationModal = (user: RatingUser) => {
    setSelectedUser(user);
    setSubscriberConfirmationModalOpen(true);
  };

  const handleSubscriberConfirmationClose = (needsUpdate: boolean) => {
    setSelectedUser(undefined);
    setSubscriberConfirmationModalOpen(false);
    if (needsUpdate) {
      fetchUsers();
    }
  };

  const nameRenderer = (user: RatingUser) => (
    <LinkButton
      to="#"
      label={`${user.firstName || '-'} ${user.lastName || ''}`}
      color="primary"
      onClick={() => showuserStatusLog(user)}
    />
  );

  const userStatusRenderer = (userStatus: any) => {
    return <UserStatusIndicator status={userStatus as UserStatus} />;
  };

  const subscriberConfirmationRenderer = (confirmation: SubscriberConfirmation) => {
    return confirmation ? <SubscriberConfirmationIndicator status={confirmation} /> : '-';
  };

  const accessRenderer = (access: boolean) => {
    return access ? <StatusBadge status="CHECKED" /> : <StatusBadge status="CROSSED" />;
  };

  const organisationRenderer = (organisationName: string) => organisationName || '-';

  const dateRenderer = (statusDate: string) => formatDate(statusDate);

  const statusRenderer = (status: RatingStatus) => <RatingStatusIndicator status={status} />;

  const initialColumns: ColumnDefinition[] = [
    {
      name: 'name',
      label: t('ADMIN.RSR.NAME'),
      sortable: true,
      searchable: true,
      getSearchValue: (value) => `${value.firstName} ${value.lastName}`,
      customBodyRender: nameRenderer,
    },
    {
      name: 'email',
      label: t('ADMIN.RSR.EMAIL'),
      sortable: true,
    },
    {
      name: 'organisationName',
      label: t('ADMIN.RSR.ORGANISATION'),
      sortable: true,
      customBodyRender: organisationRenderer,
    },
    {
      name: 'userStatus',
      label: t('ADMIN.RSR.USER_STATUS'),
      sortable: true,
      customBodyRender: userStatusRenderer,
    },
    {
      name: 'confirmation',
      label: t('ADMIN.RSR.SUBSCRIBER_CONFIRMATION'),
      sortable: true,
      customBodyRender: subscriberConfirmationRenderer,
    },
    {
      name: 'status',
      label: t('ADMIN.RSR.STATUS'),
      sortable: true,
      customBodyRender: statusRenderer,
    },
    {
      name: 'access',
      label: t('ADMIN.RSR.ACCESS'),
      customBodyRender: accessRenderer,
    },
    {
      name: 'statusDate',
      label: t('ADMIN.RSR.DATE'),
      sortable: true,
      customBodyRender: dateRenderer,
    },
    {
      name: 'moreActions',
      label: '              ',
      customBodyRender: (user) => <ActionsMenu list={getActions(user)} />,
    },
  ];

  const {
    tableActions,
    selectToggle,
    selectedIds,
    searchedValue,
    sort,
    rows,
    loading,
    setLoading,
    pagination,
    setRows,
    isSelectedAll,
    selectAllToggle,
    clearSelectedIds,
    setSelectedIds,
    setTotal,
  } = useRSR({ initialColumns, defaultSortField: 'name' });

  useEffect(() => {
    const refresh = async () => {
      setLoading(true);
      await fetchRating();
      await fetchUsers();
    };
    refresh();
  }, [ratingId, pagination, sort]);

  const fetchDebounce = useDebouncedCallback(fetchUsers, 300);

  useEffect(() => {
    fetchDebounce();
  }, [searchedValue]);

  const approve = () => {
    setConfirmType('APPROVE');
    setIsConfirmModalOpen(true);
  };

  const revoke = () => {
    setConfirmType('REVOKE');
    setIsConfirmModalOpen(true);
  };

  const headerActions = [
    {
      icon: <LockIcon />,
      label: 'REVOKE SELECTED USERS',
      onClick: revoke,
    },
    {
      icon: <UnlockIcon />,
      label: 'APPROVE SELECTED USERS',
      onClick: approve,
    },
  ];

  const buildRows = (data: RatingUser[]): RowData[] =>
    data.map((user) => {
      return {
        id: user.userId,
        data: {
          name: user,
          email: user.email,
          organisationName: user.organisationName,
          userStatus: user.userStatus,
          confirmation: user.confirmation,
          status: user.ratingStatus,
          access:
            user.confirmation === SubscriberConfirmations.SIGNED &&
            user.ratingStatus === RatingStatuses.APPROVED &&
            user.userStatus === UserStatuses.ACTIVE,
          statusDate: user.ratingStatusDate,
          moreActions: user,
        },
      };
    });

  const getActions = (item: RatingUser) => {
    return [
      {
        label: t('ADMIN.RSR.VIEW_LOG'),
        onClick: () => showuserStatusLog(item),
        icon: <ClockIcon />,
      },
      {
        label: t('ADMIN.RSR.SET_SUBSCRIBER_CONFIRMATION'),
        onClick: () => showSubscriberConfirmationModal(item),
        icon: <RefreshIcon />,
      },
      {
        label:
          item.ratingStatus === RatingStatuses.APPROVED
            ? t('ADMIN.RSR.REVOKE')
            : t('ADMIN.RSR.APPROVE'),
        onClick: () => {
          clearSelectedIds();
          setSelectedIds([item.userId]);
          if (item.ratingStatus === RatingStatuses.APPROVED) revoke();
          else approve();
        },
        icon: item.ratingStatus === RatingStatuses.APPROVED ? <LockIcon /> : <UnlockIcon />,
      },
    ];
  };

  const confirmModalData = {
    APPROVE: {
      title: 'Approve selected users',
      text: 'Do you really want to approve access for all selected users?',
      confirmLabel: 'Approve selected',
    },
    REVOKE: {
      title: 'Revoke selected',
      text: 'Do you really want to revoke access for all selected users?',
      confirmLabel: 'Revoke selected',
    },
  };

  return (
    <>
      {rating && (
        <>
          <Box my={2}>
            <Card padded>
              <Box display="flex" justifyContent="space-between">
                <Box display="flex" gridGap={1}>
                  <IconButton onClick={() => navigate(-1)}>
                    <ArrowLeft />
                  </IconButton>
                  <Typography variant="h1">
                    {rating.entityName} {rating.ratingType ? ` - ${rating.ratingType}` : ''}
                  </Typography>
                </Box>
                <Box display="flex" gridGap={1}>
                  <IconTextButton
                    icon={<AddUserIcon width={32} height={32} />}
                    iconSize="regular"
                    onClick={() => {
                      setAddModalOpen(true);
                    }}
                  >
                    ASSIGN USER
                  </IconTextButton>
                </Box>
              </Box>
            </Card>
          </Box>
          <DataTable
            loading={loading}
            headerActions={headerActions}
            selection={{
              selectedIds,
              selectToggle,
              isSelectedAll,
              selectAllToggle,
            }}
            title="Users"
            cols={initialColumns}
            rows={rows}
            selectable
            paginate
            tableActions={tableActions}
          />
          <AssignUser
            ratingName={rating.entityName}
            open={isAddModalOpen}
            onClose={handleAddUser}
            ratingId={rating.ratingId}
            ratingType={rating.entityType}
            subscriptionRatingType={ratingType}
          />
          <UserRatingStatusLog
            open={isLogModalOpen}
            onClose={() => {
              setLogModalOpen(false);
              setSelectedUser(undefined);
            }}
            ratingId={rating.ratingId}
            userId={selectedUser?.userId}
            ratingType={ratingType}
          />
          <SetSubscriberConfirmationModal
            open={isSubscriberConfirmationModalOpen}
            onClose={handleSubscriberConfirmationClose}
            user={selectedUser}
          />
          <ConfirmModal
            variant={confirmType === 'REVOKE' ? 'warning' : 'primary'}
            title={confirmModalData[confirmType].title}
            text={confirmModalData[confirmType].text}
            confirmLabel={confirmModalData[confirmType].confirmLabel}
            onClose={(v) => {
              clearSelectedIds();
              setIsConfirmModalOpen(false);
            }}
            onConfirm={() => {
              if (confirmType === 'REVOKE') {
                deactivateUsers(selectedIds);
              } else {
                activateUsers(selectedIds);
              }
              setIsConfirmModalOpen(false);
              clearSelectedIds();
            }}
            open={isConfirmModalOpen}
          />
        </>
      )}
    </>
  );
};
